import { useState } from 'react';
import { useInView } from '../../hooks/useInView';

interface LazyImgProps {
  src: string;
  alt: string;
  /** 外層容器的 className（處理尺寸與定位） */
  containerClassName?: string;
  /** img 本身的 className（處理 object-fit、hover 等） */
  imgClassName?: string;
  /** img 本身的 inline style（objectPosition、transform 等） */
  imgStyle?: React.CSSProperties;
  /** skeleton 底色，預設淺灰 */
  skeletonColor?: string;
}

/**
 * 帶 Intersection Observer 懶載入的圖片元件
 * - 進入可視區域前顯示 skeleton 佔位
 * - 進入後才發起 HTTP 請求載入圖片
 * - 載入完成後以 fade-in 顯示
 */
export default function LazyImg({
  src,
  alt,
  containerClassName = 'w-full h-full',
  imgClassName = 'w-full h-full object-cover',
  imgStyle,
  skeletonColor = '#f3f4f6',
}: LazyImgProps) {
  const { ref, inView } = useInView<HTMLDivElement>({ rootMargin: '400px 0px' });
  const [loaded, setLoaded] = useState(false);

  return (
    <div ref={ref} className={`relative overflow-hidden ${containerClassName}`}>
      {/* Skeleton 佔位：進入視野前 或 圖片尚未載入完成時顯示 */}
      {(!inView || !loaded) && (
        <div
          className="absolute inset-0 animate-pulse flex items-center justify-center"
          style={{ backgroundColor: skeletonColor }}
          aria-hidden="true"
        >
          <i className="ri-image-line text-gray-300 text-xl"></i>
        </div>
      )}

      {/* 實際圖片：進入視野後才設定 src，載入後 fade-in */}
      {inView && (
        <img
          src={src}
          alt={alt}
          className={`${imgClassName} transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          style={imgStyle}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
        />
      )}
    </div>
  );
}
